import { useEffect, useState } from "react";
import { supabase } from "../lib/supabaseClient";

export default function CardView({ cardCode }) { 
  const code = cardCode; 
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCard = async () => {
      try {
        const { data: qrData } = await supabase
          .from("qr_codes")
          .select("*")
          .eq("code", code)
          .single();
        
        if (qrData) setData(qrData);
      } catch (err) {
        console.error("Error fetching card:", err);
      } finally {
        setLoading(false);
      }
    };
    if (code) fetchCard();
  }, [code]);

  const saveVCard = () => {
    if (!data?.settings?.vCard) return;
    const v = data.settings.vCard;

    const vCard = [
      "BEGIN:VCARD",
      "VERSION:3.0",
      `FN:${v.firstName || ''} ${v.lastName || ''}`,
      `TITLE:${v.designation || ''}`,
      `TEL;TYPE=CELL:${v.phone || ''}`,
      `EMAIL:${v.email || ''}`,
      `ADR;TYPE=WORK:;;${v.address || ""};;;;`,
      `URL:${v.linkedin || ''}`,
      "END:VCARD"
    ].join("\n");

    const file = new Blob([vCard], { type: "text/vcard;charset=utf-8" });
    const url = window.URL.createObjectURL(file);
    
    // Attempt to open directly (better for mobile OS integration)
    window.location.href = url; 

    // Fallback download
    setTimeout(() => {
      const link = document.createElement("a");
      link.href = url;
      link.download = `${v.firstName || 'Contact'}.vcf`;
      link.click();
    }, 100);
  };

  if (loading) return (
    <div className="min-h-screen bg-[#020617] flex items-center justify-center">
       <div className="text-blue-500 font-black italic uppercase animate-pulse tracking-widest text-xs">
          Accessing Digital Card...
       </div>
    </div>
  );

  if (!data) return (
    <div className="min-h-screen bg-[#020617] flex items-center justify-center text-white font-black uppercase italic tracking-tighter">
      404 // Card Not Found
    </div>
  );

  const v = data.settings?.vCard || {};

  return (
    <div className="min-h-screen bg-[#020617] text-white font-sans p-6 flex flex-col items-center">
      <div className="w-full max-w-md mt-10 text-center">
        
        {/* Profile / Logo Section */}
        <div className="w-32 h-32 bg-gradient-to-tr from-blue-600 to-emerald-400 rounded-[40px] mx-auto mb-6 p-1 shadow-2xl">
          <div className="w-full h-full bg-[#0f172a] rounded-[37px] flex items-center justify-center overflow-hidden">
             {data.design?.logo ? (
               <img src={data.design.logo} className="w-full h-full object-cover" alt="Profile" />
             ) : (
               <span className="text-4xl font-black">{v.firstName?.[0] || 'D'}</span>
             )}
          </div>
        </div>
        
        <h1 className="text-4xl font-black italic uppercase tracking-tighter">
          {v.firstName} {v.lastName}
        </h1>
        <p className="text-blue-400 font-bold uppercase tracking-widest text-[10px] mt-2">
          {v.designation || "Digital Professional"}
        </p>
        
        <div className="grid grid-cols-1 gap-4 mt-10">
          {/* Main Action: Save Contact */}
          <button 
            onClick={saveVCard} 
            className="w-full bg-white text-black font-black py-5 rounded-2xl uppercase italic text-sm shadow-xl active:scale-95 transition-all"
          >
            Save to Contacts
          </button>
          
          {/* WhatsApp Action */}
          {v.phone && (
            <a 
              href={`https://wa.me/${v.phone.replace(/\D/g,'')}`} 
              className="w-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 font-black py-5 rounded-2xl uppercase text-xs flex items-center justify-center gap-3 hover:bg-emerald-500/20 transition-all"
            >
              WhatsApp Me
            </a>
          )}

          {/* Social Media Section */}
          <div className="grid grid-cols-1 gap-3 mt-2">
            {v.instagram && (
              <a 
                href={`https://instagram.com/${v.instagram.replace('@', '')}`} 
                target="_blank" 
                rel="noreferrer"
                className="w-full bg-gradient-to-r from-purple-600 to-pink-500 text-white font-black py-4 rounded-2xl uppercase text-[10px] tracking-widest text-center shadow-lg"
              >
                Instagram
              </a>
            )}

            {v.linkedin && (
              <a 
                href={v.linkedin.startsWith('http') ? v.linkedin : `https://linkedin.com/in/${v.linkedin}`} 
                target="_blank" 
                rel="noreferrer"
                className="w-full bg-[#0077b5] text-white font-black py-4 rounded-2xl uppercase text-[10px] tracking-widest text-center shadow-lg"
              >
                LinkedIn
              </a>
            )}

            {v.facebook && (
              <a 
                href={v.facebook.startsWith('http') ? v.facebook : `https://facebook.com/${v.facebook}`} 
                target="_blank" 
                rel="noreferrer"
                className="w-full bg-[#1877F2] text-white font-black py-4 rounded-2xl uppercase text-[10px] tracking-widest text-center shadow-lg"
              >
                Facebook
              </a>
            )}
          </div>

          {v.email && (
            <a 
              href={`mailto:${v.email}`} 
              className="w-full bg-white/5 border border-white/10 text-white font-black py-4 rounded-2xl uppercase text-[10px] tracking-widest text-center hover:bg-white/10 transition-all mt-2"
            >
              Send Email
            </a>
          )}
        </div>

        {v.address && (
          <div className="mt-10 p-6 bg-white/[02] border border-white/5 rounded-[32px] text-left">
            <p className="text-[10px] text-gray-400 font-black uppercase mb-2 tracking-widest">Location</p>
            <p className="text-sm text-slate-300 italic">{v.address}</p>
          </div>
        )}
      </div>

      <div className="mt-auto py-10">
        <p className="text-[9px] font-black text-gray-600 uppercase tracking-[8px]">Powered by Digitoly</p>
      </div>
    </div>
  );
}